import { ClientError, GraphQLClient } from 'graphql-request'
import { captureException } from '@sentry/react'
import { Kind, visit } from 'graphql'
import { getAuth } from './auth'
import type { DocumentNode } from 'graphql'

import type { RequestMiddleware, ResponseMiddleware } from 'graphql-request'

const TYPENAME_FIELD = {
  kind: Kind.FIELD,
  name: { kind: Kind.NAME, value: '__typename' },
} as const

// Adds __typename to every selection set, matching Apollo Client's behavior
function addTypenames(document: DocumentNode): DocumentNode {
  return visit(document, {
    SelectionSet(node) {
      const hasTypename = node.selections.some(
        (s) => s.kind === Kind.FIELD && s.name.value === '__typename',
      )

      if (hasTypename) return undefined

      return { ...node, selections: [...node.selections, TYPENAME_FIELD] }
    },
  })
}

const ENDPOINT = `${import.meta.env.VITE_API_URL!}admin/graphql`

/**
 * Injects Auth0 Bearer token and audience header, and appends
 * `?op=OperationName` to the request URL for backend routing / logging.
 */
const requestMiddleware: RequestMiddleware = (request) => {
  const token = getAuth()
  const headers = new Headers(request.headers)

  headers.set('Authorization', `Bearer ${token}`)
  headers.set('X-Auth0-Audience', import.meta.env.VITE_AUTH0_AUDIENCE)

  const url = request.operationName
    ? `${request.url}?op=${request.operationName}`
    : request.url

  return { ...request, headers, url }
}

/**
 * Handles error responses:
 * - 401 → redirect to /logout
 * - Other HTTP errors (production) → Sentry capture
 * - GraphQL errors → console.group formatted logging
 */
const responseMiddleware: ResponseMiddleware = (response) => {
  // Network / HTTP errors arrive as ClientError instances
  if (response instanceof ClientError) {
    const status = response.response.status

    if (status === 401) {
      console.info('[Auth0] Graphql Unauthorized')
      history.pushState({}, '', '/logout')

      return
    }

    if (import.meta.env.PROD) {
      captureException(`Network error: ${status}`, {
        fingerprint: ['network-error'],
        extra: {
          statusCode: status,
          errors: JSON.stringify(response.response.errors, null, 2),
        },
      })
    }

    return
  }

  // Non-ClientError Error instances (e.g. network failures)
  if (response instanceof Error) {
    console.warn(response)

    if (import.meta.env.PROD) {
      captureException(response, {
        fingerprint: ['network-error'],
      })
    }

    return
  }

  // Successful response — check for GraphQL-level errors
  if (response.errors?.length) {
    // Operation name not available in responseMiddleware.
    // The request URL contains ?op=OperationName for identification.
    for (const graphqlError of response.errors) {
      const { message, locations, path } = graphqlError

      const errorMessage = `[GraphQL error]:\
        \n  Message: ${message},\
        \n  Location: ${JSON.stringify(locations, null, 2)},\
        \n  Path: ${path}`

      console.group('[GraphQL error]')
      console.error(errorMessage)
      console.groupEnd()
    }
  }
}

/**
 * graphql-request client configured with Auth0 auth, operation name
 * URL params, error handling, and automatic __typename injection.
 */
const baseClient = new GraphQLClient(ENDPOINT, {
  requestMiddleware,
  responseMiddleware,
})

type RequestOptions = { signal?: AbortSignal }

export const graphqlClient = {
  request: (document: any, variables?: any, options?: RequestOptions) => {
    const typedDocument = addTypenames(document)

    if (options?.signal) {
      return baseClient.request({
        document: typedDocument,
        variables,
        signal: options.signal,
      })
    }

    return baseClient.request(typedDocument, variables)
  },
}
